import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Drawer,
  Tooltip,
  useTheme,
  IconButton,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon
} from '@mui/material';
import FiltersSidebar from '../components/assets/FiltersSidebar';
import AssetOperations from '../components/assets/AssetOperations';
import GenerateCsvModal from '../components/csv/GenerateCsvModal';
import { subscribeToSystem, describeRealtimeEvent } from '../services/realtime';
import { useRealtimeToast } from '../context/RealtimeToastContext';
import useMediaQuery from '@mui/material/useMediaQuery';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RoleBasedAccess, { userHasAccess } from '../services/RoleBasedAccess';
import PaginationFooter from '../components/assets/PaginationFooter';
import { AssetsProvider, useAssetsContext } from '../context/AssetsContext';
import { hasActiveAssetFilters } from '../components/assets/config';
import AssetCardList from '../components/assets/AssetCardList';
import AssetDesktopTable from '../components/assets/AssetDesktopTable';
import AddAssetModal from '../components/asset/AddAssetModal/ModalContainer'
import FilterListIcon from '@mui/icons-material/FilterList';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import { useSession } from '../context/SessionContext';
import { alpha } from '@mui/material/styles';
import { useSearchParams } from 'react-router-dom';

const Assets: React.FC = () => {
  return (
    <AssetsProvider>
      <AssetsContent />
    </AssetsProvider>
  );
};

const AssetsContent: React.FC = () => {
  const theme = useTheme();
  const accent = theme.palette.status.accent;
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const { user, selectedSystem, systems, selectSystem } = useSession();
  const [searchParams] = useSearchParams();
  const urlSystemAppliedRef = useRef(false);
  const assetsState = useAssetsContext();
  const { notify } = useRealtimeToast();

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isAddAssetModalOpen, setIsAddAssetModalOpen] = useState(false);
  const [isSpeedDialOpen, setIsSpeedDialOpen] = useState(false);
  const [isCsvModalOpen, setIsCsvModalOpen] = useState(false);


  useEffect(() => {
    if (urlSystemAppliedRef.current) return;
    if (!systems.length) return;

    const urlSystemCode = searchParams.get('system');
    if (!urlSystemCode) {
      urlSystemAppliedRef.current = true;
      return;
    }

    const normalizedUrlCode = urlSystemCode.replace(/U\d{2}$/, '');
    const target = systems.find((system) => system.systemCode === normalizedUrlCode) || null;

    if (target && target.systemCode !== selectedSystem?.systemCode) {
      selectSystem(target);
    }

    urlSystemAppliedRef.current = true;
  }, [searchParams, selectedSystem?.systemCode, selectSystem, systems]);

  // Real-time: refetch the asset list when assetDelivery publishes an
  // "asset added" event for the selected system (replaces the old 60s polling).
  // Bursts (a bulk upload, or rapid publishes) are DEBOUNCED into a single
  // refetch + toast so the page doesn't refresh once per message.
  // Re-subscribes when the selected system changes.
  const assetEventTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastAssetEvent = useRef<any>(null);
  // Hold the latest handlers in refs so the subscription effect doesn't depend on
  // their (frequently-changing) identities — avoids tearing down/rebuilding the IoT
  // subscription on every list/filter/page change, while still calling the latest.
  const applyAssetEventRef = useRef(assetsState.applyAssetEvent);
  applyAssetEventRef.current = assetsState.applyAssetEvent;
  const applyAssetStatusRef = useRef(assetsState.applyAssetStatus);
  applyAssetStatusRef.current = assetsState.applyAssetStatus;
  const refetchRef = useRef(assetsState.refetch);
  refetchRef.current = assetsState.refetch;
  useEffect(() => {
    const systemCode = selectedSystem?.systemCode;
    if (!systemCode) return;

    let sub: { unsubscribe: () => void } | null = null;
    let runSub: { unsubscribe: () => void } | null = null;
    let cancelled = false;
    subscribeToSystem(
      systemCode,
      (data) => {
        console.log("[realtime] asset event:", data);
        const status = (data as any)?.status;
        if (status) {
          // Live processing/syndication progress → patch the row in place. No
          // refetch, no reorder. Toast only on ERROR (routine step progress is
          // silent — describeRealtimeEvent returns "" for it).
          applyAssetStatusRef.current((data as any).asset_id, status);
          const msg = describeRealtimeEvent(data);
          if (msg) notify(msg);
          return;
        }
        const asset = (data as any)?.asset;
        if (asset) {
          // Inline row → insert immediately (no refetch). Patches are cheap and
          // each carries its own row, so no need to debounce a burst.
          applyAssetEventRef.current(asset);
          notify(describeRealtimeEvent(data));
          return;
        }
        // No inline row (older publisher / oversized / update / delete) → debounce
        // a burst of events into a single refetch + toast.
        lastAssetEvent.current = data;
        if (assetEventTimer.current) clearTimeout(assetEventTimer.current);
        assetEventTimer.current = setTimeout(() => {
          refetchRef.current();
          notify(describeRealtimeEvent(lastAssetEvent.current));
        }, 1000); // collapse a burst of events into one refresh
      },
      (err) => console.error("[realtime] asset subscription error:", err),
      "assets"
    )
      .then((s) => {
        if (cancelled) s.unsubscribe();
        else sub = s;
      })
      .catch((err) => console.error("[realtime] asset subscribe failed:", err));

    // Separate subscription for the unit's run acknowledgement. Asset rows arrive on
    // systems/<code>/assets; runStatus is its own topic, so the /assets subscription
    // above never sees it — subscribe to it explicitly and just toast the outcome
    // (received / rejected / accepted). No row changes.
    subscribeToSystem(
      systemCode,
      (data) => {
        const msg = describeRealtimeEvent(data);
        if (msg) notify(msg);
      },
      (err) => console.error("[realtime] runStatus subscription error:", err),
      "runStatus"
    )
      .then((s) => {
        if (cancelled) s.unsubscribe();
        else runSub = s;
      })
      .catch((err) => console.error("[realtime] runStatus subscribe failed:", err));

    return () => {
      cancelled = true;
      sub?.unsubscribe();
      runSub?.unsubscribe();
      if (assetEventTimer.current) clearTimeout(assetEventTimer.current);
    };
  }, [selectedSystem?.systemCode, notify]);

  const filtersApplied = hasActiveAssetFilters(assetsState.filters);

  const sidebarWidth = isSidebarOpen
    ? (isMobile ? '70vw' : '280px')
    : (isMobile ? '12vw' : '60px');

  // Just close — no manual refetch. Both flows are covered by real-time now:
  // run-asset by the assetDelivery event (when the unit delivers), and manual
  // add by the finalize_asset lambda publishing the same systems/<code>/assets
  // event. The Assets subscription below refetches on either.
  const handleAddAssetModalClose = () => {
    setIsAddAssetModalOpen(false);
  };


  return (
    <Box sx={{ display: 'flex', height: '100%', minHeight: 0, overflow: 'hidden' }}>
        {!isMobile && (
          <Box
            sx={{
              height: '100%',
              overflowY: 'auto',
              width: sidebarWidth,
              transition: 'width 0.3s ease-in-out',
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.text.primary,
              display: 'flex',
              flexDirection: 'column',
              px: isSidebarOpen ? 2 : 0,
              zIndex: 100,
              flexShrink: 0
            }}
          >
            {isSidebarOpen ? (
              <FiltersSidebar
                toggleSidebar={() => setIsSidebarOpen(false)}
              />
            ) : (
              <Box display="flex" flexDirection="column">
                <Tooltip title="Open Filters" placement="right" arrow>
                  <IconButton
                    data-cy="assets-open-filters-btn"
                    onClick={() => setIsSidebarOpen(true)}
                    sx={{
                      m: 1,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 5,
                      color: filtersApplied ? '#fff' : theme.palette.text.primary,
                      backgroundColor: filtersApplied ? accent : 'transparent',
                      '&:hover': {
                        backgroundColor: filtersApplied ? alpha(accent, 0.9) : theme.palette.action.hover,
                        borderColor: filtersApplied ? accent : theme.palette.text.primary,
                      },
                    }}
                  >
                    <FilterAltOutlinedIcon />
                  </IconButton>
                </Tooltip>


                <RoleBasedAccess allowedRoles={['Admin', 'Support', 'Auditor']}>
                  <Tooltip title="Add New Asset" placement="right" arrow>
                    <IconButton
                      data-cy="assets-add-asset-btn"
                      onClick={() => setIsAddAssetModalOpen(true)}
                      sx={{
                        m: 1,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 5,
                        color: theme.palette.text.primary,
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                          borderColor: theme.palette.text.primary,
                        },
                      }}
                    >
                      <AddCircleOutlineIcon />
                    </IconButton>
                  </Tooltip>
                </RoleBasedAccess>

                <Tooltip title="Generate CSV" placement="right" arrow>
                  <IconButton
                    data-cy="assets-generate-csv-btn"
                    onClick={() => setIsCsvModalOpen(true)} // It controls the parent's state
                    sx={{
                      m: 1,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 5,
                      color: theme.palette.text.primary,
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                        borderColor: theme.palette.text.primary,
                      },
                    }}
                  >
                    <DescriptionIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        )}

        {/* Main Content */}
        <Box
          sx={{
            flex: 1,
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            padding: 1,
            color: 'text.primary',
            overflow: 'hidden'
          }}
        >

          {!selectedSystem ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', textAlign: 'center', px: 2 }}>
              <Box>
                <Box sx={{ fontSize: 18, fontWeight: 600, mb: 1 }}>
                  No systems assigned
                </Box>
                <Box sx={{ opacity: 0.8 }}>
                  Your account doesn’t have access to any systems.
                </Box>
                <Box sx={{ opacity: 0.8 }}>
                  Please select a system or contact support to get access.
                </Box>
              </Box>
            </Box>
          ) : assetsState.assets.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', textAlign: 'center', px: 2 }}>
              <Box>
                <Box sx={{ fontSize: 18, fontWeight: 600, mb: 1 }}>
                  No assets found
                </Box>
                <Box sx={{ opacity: 0.8 }}>
                  Try widening your date range or clearing filters.
                </Box>
                <Box sx={{ opacity: 0.8 }}>
                  Please check your filter settings.
                </Box>
              </Box>
            </Box>
          ) : (
            <>
              <Box sx={{
                flex: 1,
                overflowY: 'auto',
                paddingBottom: '20px',
                scrollbarWidth: 'none',        // Firefox
                '&::-webkit-scrollbar': {
                  display: 'none',             // Chrome, Safari
                },
              }}>
                <RoleBasedAccess allowedRoles={['Admin', 'Support', 'Auditor', 'Client Plus']}>
                  <AssetOperations />
                </RoleBasedAccess>
                {isMobile ? <AssetCardList /> : <AssetDesktopTable />}
              </Box>

              {/* Pagination + Results Per Page */}
              <PaginationFooter />
            </>
          )}
        </Box>

        {isMobile && (
          <>
            <SpeedDial
              data-cy="assets-speed-dial"
              ariaLabel="Asset Actions SpeedDial"
              sx={{ position: 'fixed', bottom: 16, right: 16 }}
              icon={<SpeedDialIcon icon={<FilterListIcon />} openIcon={<CloseIcon />} />}
              onClose={() => setIsSpeedDialOpen(false)}
              onOpen={() => setIsSpeedDialOpen(true)}
              open={isSpeedDialOpen}
              // Style the main FAB based on filtersApplied
              FabProps={{
                sx: {
                  backgroundColor: filtersApplied ? accent : '#000',
                  color: filtersApplied ? '#fff' : 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: filtersApplied ? alpha(accent, 0.9) : '#000',
                  },
                },
              }}
            >
              <SpeedDialAction
                key="Filters"
                data-cy="assets-speed-dial-filters"
                icon={<FilterAltOutlinedIcon color="inherit" />}
                tooltipTitle="Filters"
                onClick={() => {
                  setIsSpeedDialOpen(false);
                  setIsFilterDrawerOpen(true);
                }}
              />

              {userHasAccess(user?.role, ['Admin', 'Support', 'Auditor']) && (
                <SpeedDialAction
                  key="Add Asset"
                  data-cy="assets-speed-dial-add-asset"
                  icon={<AddCircleOutlineIcon />}
                  tooltipTitle="Add New Asset"
                  onClick={() => {
                    setIsSpeedDialOpen(false);
                    setIsAddAssetModalOpen(true);
                  }}
                />
              )}

              <SpeedDialAction
                key="Generate CSV"
                data-cy="assets-speed-dial-csv"
                icon={<FileDownloadIcon />}
                tooltipTitle="Generate CSV"
                onClick={() => {
                  setIsSpeedDialOpen(false);
                  setIsCsvModalOpen(true); // This now controls the modal
                }}
              />
            </SpeedDial>

            <Drawer
              anchor="bottom"
              open={isFilterDrawerOpen}
              onClose={() => setIsFilterDrawerOpen(false)}
            >
              <FiltersSidebar toggleSidebar={() => setIsFilterDrawerOpen(false)} />
            </Drawer>
          </>
        )}

        {/* MODALS - Pass state down to them now */}
        <GenerateCsvModal
          open={isCsvModalOpen}
          onClose={() => setIsCsvModalOpen(false)}
        />

        <AddAssetModal
          open={isAddAssetModalOpen}
          onClose={handleAddAssetModalClose}
        />
      </Box>
  );
};

export default Assets;
